import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import isEqual from "lodash.isequal";
import React, {useEffect, useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import type {Result} from "@khanacademy/wonder-blocks-data";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import {Caption} from "@khanacademy/wonder-blocks-typography";
import {TRANSFER_AUTH_TOKEN_MUTATION} from "@repo/auth-shared";
import {useMutation} from "@repo/data-gql";
import {Errors, KAError} from "@repo/logging";
import {
    getTransferAuthUrl,
    isKhanAcademyUrl,
    preserveMarketingParams,
    redirect,
    useLocation,
} from "@repo/urls";
import {
    RequireReauth,
    SET_BASIC_SETTINGS_MUTATION,
    type BasicSettingsData,
    type Role,
    type SetBasicSettingsType,
} from "@repo/user-settings";

import StudentBasicSettings from "#class-package/student/student-settings-basics.tsx";
import {isDateOverAgeGate} from "#shared-package/is-over-age-gate.ts";
import {getLocaleStatus} from "#shared-package/locale-status.ts";

type InitialSettings = Pick<
    BasicSettingsData,
    "nickname" | "username" | "birthdate" | "userLanguage" | "region"
>;

interface Props {
    actorIdentifier: string | null | undefined;
    actorIsChild: boolean;
    targetIsChild: boolean;
    isModifyingChild: boolean;
    initialSettings: InitialSettings;
    kaid: string;
    userChosenRoles: Array<Role>;
    hasDistrictProvidedBirthdate: boolean;
    successfullyChangedSettings: () => unknown;
    includesDistrictOwnedData: boolean;
    includesCentrallyOwnedDistrictData: boolean;
    isClasslinkUser: boolean;
}

const BasicSettings: React.FC<Props> = ({
    actorIdentifier,
    actorIsChild,
    targetIsChild,
    isModifyingChild,
    initialSettings,
    kaid,
    userChosenRoles,
    hasDistrictProvidedBirthdate,
    successfullyChangedSettings,
    includesDistrictOwnedData,
    includesCentrallyOwnedDistrictData,
    isClasslinkUser,
}) => {
    const location = useLocation();
    const queryParams = location.queryParams;
    const [showErrors, setShowErrors] = useState(true);
    const [birthdateError, setBirthdateError] = useState<string | null>(null);
    const [nicknameError, setNicknameError] = useState<string | null>(null);
    const [usernameError, setUsernameError] = useState<string | null>(null);
    const [reauthError, setReauthError] = useState<string | null>(null);
    const [isUnexpectedError, setIsUnexpectedError] = useState(false);
    const [isSavingSettings, setIsSavingSettings] = useState(false);
    const [currentSettings, setCurrentSettings] =
        useState<BasicSettingsData>(initialSettings);
    const [isMounted, setIsMounted] = useState(false);
    const [transferAuthTokenMutation] = useMutation(
        TRANSFER_AUTH_TOKEN_MUTATION,
    );
    const [setBasicSettingsMutation] = useMutation(SET_BASIC_SETTINGS_MUTATION);
    // We need to keep track of isMounted so that we only
    // set the state after the storeBasicSettings promise
    // resolves IF the component is mounted.
    useEffect(() => {
        setIsMounted(true);
        return () => {
            setIsMounted(false);
        };
    }, []);
    useEffect(() => {}, [usernameError]);

    const handleSettingsChange = (newSettings: Partial<BasicSettingsData>) => {
        if (newSettings.birthdate) {
            const confirmed =
                !isModifyingChild && !isDateOverAgeGate(newSettings.birthdate)
                    ? // eslint-disable-next-line no-alert -- ignore this plz
                      window.confirm(
                          t`If you change your birthdate to be younger than 13, you will not be able to change it again until you turn 13. Is this OK?`,
                      )
                    : true;

            if (!confirmed) {
                return;
            }
        }

        setShowErrors(false);
        setCurrentSettings((prevSettings) => ({
            ...prevSettings,
            ...newSettings,
        }));
    };

    const storeSettingsAndModifyState = async (
        initialSettings: BasicSettingsData,
        currentSettings: BasicSettingsData,
    ) => {
        let result: Result<SetBasicSettingsType["response"]> | null;
        try {
            result = await setBasicSettingsMutation({...settings, kaid});
        } catch (e) {
            throw new KAError("Failed to set basic settings", Errors.Internal, {
                cause: e as Error,
            });
        }
        if (result.status === "success") {
            const errors = result.data.setSettings?.errors || [];
            const errorCodes = errors.map((e) => e?.code);

            if (errorCodes.length > 0) {
                if (errorCodes.includes("NICKNAME_ERROR")) {
                    setNicknameError(t`Please pick a different name.`);
                }
                if (errorCodes.includes("USERNAME_ERROR")) {
                    setUsernameError(t`That username is unavailable.`);
                }
                if (errorCodes.includes("BIRTHDATE_ERROR")) {
                    setBirthdateError(t`Invalid birthdate.`);
                }
                if (errorCodes.includes("REAUTH_REQUIRED")) {
                    setReauthError(
                        t`Reauthentication required to perform this action. Please try again.`,
                    );
                }
                return;
            }
        }

        if (
            currentSettings.userLanguage !== initialSettings.userLanguage &&
            !isModifyingChild
        ) {
            // If we changed the language, we'll redirect them
            // to this page in their language.
            const result = await transferAuthTokenMutation({});
            let transferAuthToken: string | null = null;
            if (result.status === "success") {
                transferAuthToken =
                    result.data.createTransferAuthToken?.token || null;
            }
            if (transferAuthToken !== null) {
                const transferAuthUrl = getTransferAuthUrl(
                    transferAuthToken,
                    "/settings/account",
                    currentSettings.userLanguage,
                    currentSettings.userLanguage
                        ? // NOTE(kevinb): This is the only place
                          // where we use getLocalestatus().  All
                          // other calls to getTransferAuthUrl() are
                          // places where we know the status because
                          // we're using the stored in the user profile.
                          getLocaleStatus(
                              currentSettings.userLanguage as string,
                          )
                        : null,
                );
                redirect(
                    preserveMarketingParams(
                        transferAuthUrl,
                        new URLSearchParams(location.search),
                    ),
                );
                return;
            }
            // If there's no transferAuthToken, we fall through.
        }

        if (queryParams.continue && isKhanAcademyUrl(queryParams.continue)) {
            redirect(
                preserveMarketingParams(
                    queryParams.continue,
                    new URLSearchParams(location.search),
                ),
            );
            return;
        }

        successfullyChangedSettings();
    };

    const setSettings = async (
        initialSettings: BasicSettingsData,
        currentSettings: BasicSettingsData,
    ) => {
        setIsSavingSettings(true);
        setNicknameError(null);
        setUsernameError(null);
        setBirthdateError(null);
        setReauthError(null);

        try {
            await storeSettingsAndModifyState(initialSettings, currentSettings);
            if (isMounted) {
                setIsSavingSettings(false);
            }
        } catch {
            setIsUnexpectedError(true);
            setIsSavingSettings(false);
        }
    };

    const setSettingsAndClearErrors = () => {
        const newSettings = {...currentSettings};

        // If the nickname and/or username has been changed, ask for
        // confirmation first. If the user does not OK the alert,
        // reset the field.
        if (
            initialSettings.nickname !== currentSettings.nickname &&
            // eslint-disable-next-line no-alert -- allow alerts
            !confirm(t`Are you sure you want to change your nickname?`)
        ) {
            newSettings.nickname = initialSettings.nickname;
        }

        if (
            initialSettings.username !== currentSettings.username &&
            // eslint-disable-next-line no-alert -- allow alerts
            !confirm(t`Are you sure you want to change your username?`)
        ) {
            newSettings.username = initialSettings.username;
        }

        // Even though this is an async function, we don't await it;
        // we let it run in the background and update state when it's
        // finished.
        setSettings(initialSettings, newSettings);
        setShowErrors(true);
        setCurrentSettings(newSettings);
    };

    const maybeRenderReauthError = () => {
        if (!showErrors || !reauthError) {
            return null;
        }
        return <Caption style={styles.error}>{reauthError}</Caption>;
    };

    const renderSaveBasicSettingsButton = () => {
        const isSaveChangesDisabled =
            isSavingSettings ||
            isEqual(initialSettings, currentSettings) ||
            !!usernameError;

        const submitButton = (onClick: () => unknown) => (
            <Button
                onClick={onClick}
                style={styles.button}
                spinner={isSavingSettings}
                disabled={isSaveChangesDisabled}
                testId="basic-settings-save-button"
            >
                {t`Save changes`}
            </Button>
        );

        // We enforce reauth if the user changed their birthdate or username
        if (
            currentSettings.birthdate !== initialSettings.birthdate ||
            currentSettings.username !== initialSettings.username
        ) {
            return (
                <RequireReauth
                    identifier={actorIdentifier}
                    showClasslinkAuthProvider={isClasslinkUser}
                    onSuccess={setSettingsAndClearErrors}
                    onFailure={() => {}}
                >
                    {(invokeReauth) => submitButton(invokeReauth)}
                </RequireReauth>
            );
        }
        return submitButton(setSettingsAndClearErrors);
    };

    const settings = {
        ...initialSettings,
        ...currentSettings,
    };

    return (
        <React.Fragment>
            <StudentBasicSettings
                settings={settings}
                onSettingsChange={handleSettingsChange}
                birthdateError={showErrors ? birthdateError : null}
                nicknameError={showErrors ? nicknameError : null}
                includesDistrictOwnedData={includesDistrictOwnedData}
                includesCentrallyOwnedDistrictData={
                    includesCentrallyOwnedDistrictData
                }
                disabled={false}
                actorIsChildAccount={actorIsChild}
                targetIsChildAccount={targetIsChild}
                roles={userChosenRoles}
                hasDistrictProvidedBirthdate={hasDistrictProvidedBirthdate}
                showUsernameField={true}
                setUsernameError={setUsernameError}
            />
            <Strut size={24} />
            <View style={styles.buttonContainer}>
                {renderSaveBasicSettingsButton()}
                {showErrors && isUnexpectedError && (
                    <Caption style={styles.error}>
                        {t`Oops. Something went wrong. Please try again.`}
                    </Caption>
                )}
                {maybeRenderReauthError()}
            </View>
        </React.Fragment>
    );
};

const styles = StyleSheet.create({
    buttonContainer: {
        flexDirection: "row",
    },
    button: {
        alignSelf: "flex-start",
        paddingInlineStart: sizing.size_160,
        paddingInlineEnd: sizing.size_160,
    },
    error: {
        color: semanticColor.status.critical.foreground,
        marginInlineStart: sizing.size_160,
    },
});

export default React.memo(BasicSettings);
